import Box from "@mui/material/Box"
import Checkbox from "@mui/material/Checkbox"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableCell from "@mui/material/TableCell"
import TableContainer from "@mui/material/TableContainer"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import Typography from "@mui/material/Typography"
import { enqueueSnackbar } from "notistack"
import { useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"

import { ProjectFormPageProps } from "components/ProjectForm"
import { setDemolitionMaterialsForm } from "components/ProjectForm/ProjectFormSlice"
import RadioGroup from "components/RadioGroup/RadioGroup"
import { useGetDemolitionSiteMaterialsQuery } from "pages/Operations/features/Demolition/DemolitionService"
import { TStore } from "store/Store"
import ProjectFormFooter from "../../components/ProjectFormFooter"
import { tableCellStyles, tableHeaderCellStyles } from "./index"

const STATUS_COLOR_MAP: Record<string, string> = {
    commissioned: "#19B06D",
    onboarded: "#0430C0",
    installed: "#19B06D",
    dismantled: "#F94054",
}

const getStatusColor = (status?: string | null, fallback?: string | null) => {
    if (!status) return "#101941"
    const key = status.trim().toLowerCase()
    return STATUS_COLOR_MAP[key] || fallback || "#101941"
}

const DemolitionChargersAndMaterials = (props: ProjectFormPageProps) => {
    const dispatch = useDispatch()
    const { account_n_sites_form, demolition_materials_form } = useSelector((state: TStore) => state.ProjectForm)
    const sourceSiteId = account_n_sites_form?.source_site_id

    const [isEntireStation, setIsEntireStation] = useState<"yes" | "no">(
        demolition_materials_form?.is_site_demolition_req ? "yes" : "no"
    )
    const [selectedPlpIds, setSelectedPlpIds] = useState<Set<number>>(new Set(demolition_materials_form?.plp_ids || []))

    const { data, isFetching } = useGetDemolitionSiteMaterialsQuery(
        { site_id: sourceSiteId as number },
        { skip: !sourceSiteId }
    )

    const chargers = useMemo(() => data?.chargers || [], [data])

    useEffect(() => {
        if (isEntireStation === "yes") {
            setSelectedPlpIds(new Set(chargers.map(c => c.plp_id)))
        }
    }, [isEntireStation, chargers])

    const toggleAll = () => {
        if (selectedPlpIds.size === chargers.length) {
            setSelectedPlpIds(new Set())
        } else {
            setSelectedPlpIds(new Set(chargers.map(c => c.plp_id)))
        }
    }

    const toggleRow = (plpId: number) => {
        setSelectedPlpIds(prev => {
            const next = new Set(prev)
            if (next.has(plpId)) next.delete(plpId)
            else next.add(plpId)
            return next
        })
    }

    const handleSubmit = () => {
        if (selectedPlpIds.size === 0) {
            enqueueSnackbar("Please select at least one charger to proceed", { variant: "error" })
            return
        }
        dispatch(
            setDemolitionMaterialsForm({
                is_site_demolition_req: isEntireStation === "yes",
                plp_ids: Array.from(selectedPlpIds),
            })
        )
        props.handleNextStep()
    }

    const allSelected = chargers.length > 0 && selectedPlpIds.size === chargers.length
    const disableSelection = isEntireStation === "yes"

    return (
        <>
            <Box
                display={"flex"}
                flexDirection={"column"}
                gap={3}
                className="custom-scrollbar"
                maxHeight={"65vh"}
                padding={"var(--mui-modal-padding)"}
                sx={{
                    overflowY: "auto",
                }}>
                <Box display={"flex"} flexDirection={"column"} gap={1}>
                    <Typography variant="label" fontWeight={500}>
                        Is the entire station being dismantled?*
                    </Typography>
                    <RadioGroup
                        name="is_entire_station"
                        options={[
                            { id: "yes", title: "Yes" },
                            { id: "no", title: "No" },
                        ]}
                        value={{ id: isEntireStation, title: isEntireStation === "yes" ? "Yes" : "No" }}
                        onSelect={(option: { id: "yes" | "no" }) => setIsEntireStation(option.id)}
                        labelsStyle={{ display: "flex", gap: "16px" }}
                    />
                </Box>

                <Box display={"flex"} flexDirection={"column"} gap={1}>
                    <Typography variant="label" fontSize={"14px"} fontWeight={500}>
                        Site Charger List:
                    </Typography>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ ...tableHeaderCellStyles, width: "44px" }}>
                                        <Checkbox
                                            size="small"
                                            checked={allSelected}
                                            indeterminate={!allSelected && selectedPlpIds.size > 0}
                                            onChange={toggleAll}
                                            disabled={disableSelection || chargers.length === 0}
                                        />
                                    </TableCell>
                                    <TableCell sx={{ ...tableHeaderCellStyles, width: "60px" }}>S.No.</TableCell>
                                    <TableCell sx={tableHeaderCellStyles}>Serial No./CIN</TableCell>
                                    <TableCell sx={tableHeaderCellStyles}>Display Name</TableCell>
                                    <TableCell sx={tableHeaderCellStyles}>SKU Code</TableCell>
                                    <TableCell sx={tableHeaderCellStyles}>Operational Status</TableCell>
                                    <TableCell sx={tableHeaderCellStyles}>Sub Status</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {chargers.length === 0 ? (
                                    <TableRow>
                                        <TableCell
                                            colSpan={8}
                                            align="center"
                                            sx={{ ...tableCellStyles, padding: "12px", color: "#70758D" }}>
                                            {isFetching
                                                ? "Loading..."
                                                : sourceSiteId
                                                  ? "No chargers found for this site"
                                                  : "Select a source site to view chargers"}
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    chargers.map((charger, idx) => {
                                        const isChecked = selectedPlpIds.has(charger.plp_id)
                                        const opColor = getStatusColor(charger.operational_status)
                                        const subColor = getStatusColor(
                                            charger.charger_sub_status,
                                            charger.charger_sub_status_color
                                        )
                                        return (
                                            <TableRow key={charger.plp_id}>
                                                <TableCell sx={{ ...tableCellStyles, padding: "0 8px" }}>
                                                    <Checkbox
                                                        size="small"
                                                        checked={isChecked}
                                                        onChange={() => toggleRow(charger.plp_id)}
                                                        disabled={disableSelection}
                                                    />
                                                </TableCell>
                                                <TableCell sx={{ ...tableCellStyles, padding: "0 8px" }}>
                                                    {idx + 1}
                                                </TableCell>
                                                <TableCell sx={{ ...tableCellStyles, padding: "0 8px" }}>
                                                    {charger.serial_number || "-"}
                                                </TableCell>
                                                <TableCell sx={{ ...tableCellStyles, padding: "0 8px" }}>
                                                    {charger.display_name || "-"}
                                                </TableCell>
                                                <TableCell sx={{ ...tableCellStyles, padding: "0 8px" }}>
                                                    {charger.sku_code || "-"}
                                                </TableCell>
                                                <TableCell
                                                    sx={{
                                                        ...tableCellStyles,
                                                        padding: "0 8px",
                                                        color: opColor,
                                                        fontWeight: 500,
                                                    }}>
                                                    {charger.operational_status || "-"}
                                                </TableCell>
                                                <TableCell
                                                    sx={{
                                                        ...tableCellStyles,
                                                        padding: "0 8px",
                                                        color: subColor,
                                                        fontWeight: 500,
                                                    }}>
                                                    {charger.charger_sub_status || "-"}
                                                </TableCell>
                                            </TableRow>
                                        )
                                    })
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            </Box>
            <ProjectFormFooter handleCloseForm={props.handleCloseForm} handleSubmit={handleSubmit} />
        </>
    )
}

export default DemolitionChargersAndMaterials
